/**
 * MediaImage — `<img>` qui résout sa minio_key via `useMediaUrl`.
 *
 *   <MediaImage minioKey={work.thumb_minio_key} alt={work.title_fr} />
 *
 * Pendant le fetch de l'URL signée, on rend un placeholder neutre (papier
 * cassé) — pas de spinner, pas de "Loading…" : la résolution est rapide
 * (un round-trip API local) et un placeholder discret évite le flash UI.
 *
 * Quand `minioKey` est falsy (œuvre sans média encore), on rend le
 * placeholder en permanence + un éventuel fallback si fourni.
 *
 * Le composant n'expose pas l'URL crue à l'appelant — la durée de vie
 * (TanStack Query staleTime 4min < TTL 5min) est gérée par le hook.
 */

import { type ImgHTMLAttributes } from 'react'

import { useMediaUrl } from '@/hooks/useWorks'

import './MediaImage.css'

interface MediaImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  minioKey: string | null | undefined
  alt: string
  /** Element rendu quand pas de minioKey (œuvre sans média). */
  fallback?: React.ReactNode
}

export default function MediaImage({
  minioKey,
  alt,
  fallback,
  className,
  ...rest
}: MediaImageProps) {
  const { data: url, isLoading } = useMediaUrl(minioKey)

  if (!minioKey) {
    return (
      <div className={['media-image-placeholder', className].filter(Boolean).join(' ')}>
        {fallback ?? <span className="media-image-placeholder-mark" aria-hidden="true" />}
      </div>
    )
  }

  if (isLoading || !url) {
    return (
      <div
        className={['media-image-placeholder', className].filter(Boolean).join(' ')}
        aria-busy="true"
        aria-label={`Chargement de ${alt}`}
      />
    )
  }

  return (
    <img
      {...rest}
      src={url}
      alt={alt}
      loading="lazy"
      className={['media-image', className].filter(Boolean).join(' ')}
    />
  )
}
